import {
  animate,
  motion,
  type MotionStyle,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';

const ease = [0.22, 1, 0.36, 1] as const;

/** Route-level enter/exit transition; keyed by the caller so each navigation animates. */
export function PageTransition({ children, transitionKey }: { children: ReactNode; transitionKey: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      key={transitionKey}
      className="page-transition"
      initial={reduce ? false : { opacity: 0, y: 14, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.38, ease }}
    >
      {children}
    </motion.div>
  );
}

/** 3D tilt with a cursor-following glare; falls back to a flat hover lift under reduced motion. */
export function Tilt({
  children,
  className,
  style,
  max = 7,
  glare = true,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  max?: number;
  glare?: boolean;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const srx = useSpring(rx, { stiffness: 220, damping: 20 });
  const sry = useSpring(ry, { stiffness: 220, damping: 20 });
  const glareBg = useTransform([gx, gy], ([x, y]) => `radial-gradient(240px circle at ${x}% ${y}%, rgba(255,255,255,0.14), transparent 60%)`);

  const onMove = (event: MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    ry.set((px - 0.5) * max * 2);
    rx.set((0.5 - py) * max * 2);
    gx.set(px * 100);
    gy.set(py * 100);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      {...(className ? { className } : {})}
      style={{ ...(style ?? {}), rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d', transformPerspective: 900 } as MotionStyle}
      onMouseMove={onMove}
      onMouseLeave={reset}
      {...(reduce ? {} : { whileHover: { y: -6, scale: 1.015 } })}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      {children}
      {glare && !reduce ? <motion.div aria-hidden className="tilt-glare" style={{ background: glareBg }} /> : null}
    </motion.div>
  );
}

/**
 * Animates a numeric string in place (e.g. "1,240", "0.00065 BTCB"). The final text is
 * rendered immediately when motion is reduced or the value has no leading number.
 */
export function CountUp({ value, duration = 1.1, className }: { value: string; duration?: number; className?: string }) {
  const reduce = useReducedMotion();
  const match = /^(\s*)([0-9][0-9,]*)(\.[0-9]+)?(.*)$/s.exec(value);
  const [text, setText] = useState(value);
  const previous = useRef(0);

  useEffect(() => {
    if (reduce || !match) {
      setText(value);
      return;
    }
    const lead = match[1] ?? '';
    const whole = match[2] ?? '0';
    const fraction = match[3] ?? '';
    const tail = match[4] ?? '';
    const decimals = fraction ? fraction.length - 1 : 0;
    const useGrouping = whole.includes(',');
    const target = Number(`${whole.replace(/,/g, '')}${fraction}`);
    if (!Number.isFinite(target)) {
      setText(value);
      return;
    }
    const format = (current: number) =>
      `${lead}${current.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping })}${tail}`;
    const controls = animate(previous.current, target, {
      duration,
      ease: 'easeOut',
      onUpdate: (current) => setText(format(current)),
      onComplete: () => setText(value),
    });
    previous.current = target;
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduce]);

  return <span {...(className ? { className } : {})}>{text}</span>;
}
